import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { UserHover } from "Components/UserHover";
import { authService, newAuthService } from "Service/Axios";
import {
  GET_GLOBAL_NOTIFICATIONS_LIST_URL,
  UPDATE_MULTIPLE_TASK_DECISION_URL,
  updateNotificationReadStatusApi,
} from "Constant/api";
import { paths } from "Constant/paths";
import { formatDateWithFullMonthTime, getFirstLetterOfName } from "Utils";

export const Notifications = ({ toggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [allList, setAllList] = useState([]);
  const [mentionList, setMentionList] = useState([]);
  const [assigneeList, setAssigneeList] = useState([]);
  const [emailList, setEmailList] = useState([]);

  const {
    admin: {
      applicationStatus: { root: applicationStatus, chat, email },
    },
  } = paths;

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  // List
  const getAllNotifications = async (search) => {
    try {
      const payload = {};
      if (search) {
        payload.search = search;
      }
      const response = await authService(
        GET_GLOBAL_NOTIFICATIONS_LIST_URL,
        "POST",
        payload
      );
      if (response.success) {
        const { all, mention, email, assignee } = response.data;
        setAllList(
          all?.map((res) => ({
            ...res,
            key: uuid(),
          }))
        );
        setMentionList(
          mention?.map((res) => ({
            ...res,
            key: uuid(),
          }))
        );
        setEmailList(
          email?.map((res) => ({
            ...res,
            key: uuid(),
          }))
        );
        setAssigneeList(
          assignee?.map((res) => ({
            ...res,
            key: uuid(),
          }))
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  useEffect(() => {
    if (search) {
      const delayDebounceFn = setTimeout(() => {
        getAllNotifications(search);
      }, 1000);
      return () => clearTimeout(delayDebounceFn);
    } else {
      getAllNotifications();
    }
  }, [search]);

  // Handler
  const onUpdateDecision = async (taskId, taskDecision) => {
    try {
      const payload = {
        gid: [taskId],
        decision: taskDecision,
      };
      const response = await newAuthService(
        UPDATE_MULTIPLE_TASK_DECISION_URL,
        "POST",
        payload
      );
      if (response.status && response.data.success) {
        toast.success(t("common.messages.notification.success.updateStatus"));
        toggle();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      }
    }
  };

  const onUpdateReadStatus = async (taskId, type) => {
    try {
      const payload = {
        gid: taskId,
        read: true,
      };
      const response = await newAuthService(
        updateNotificationReadStatusApi,
        "POST",
        payload
      );
      if (response.status) {
        toggle();
        if (type === "mentioned") {
          navigate(`${applicationStatus}/${taskId}${chat}`);
        }
        if (type === "email") {
          navigate(`${applicationStatus}/${taskId}${email}`);
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      }
    }
  };

  return (
    <div className="notification-wrapper d-flex flex-column gap-2">
      <div className="search-div">
        <div className="input-group">
          <input
            type="search"
            className="form-control"
            placeholder="Search Notification"
            value={search}
            onChange={handleChange}
          />
          <button type="button" className="input-group-text">
            <i className="pluto-search"></i>
          </button>
        </div>
      </div>
      <ul className="nav pluto-tab mb-3" id="pluto-tab" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className="nav-link active"
            id="pluto-all-tab"
            data-bs-toggle="pill"
            data-bs-target="#pluto-all"
            type="button"
            role="tab"
            aria-controls="pluto-all"
            aria-selected="true"
          >
            All
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className="nav-link"
            id="pluto-email-tab"
            data-bs-toggle="pill"
            data-bs-target="#pluto-email"
            type="button"
            role="tab"
            aria-controls="pluto-email"
            aria-selected="false"
          >
            Email
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className="nav-link"
            id="pluto-mention-tab"
            data-bs-toggle="pill"
            data-bs-target="#pluto-mention"
            type="button"
            role="tab"
            aria-controls="pluto-mention"
            aria-selected="false"
          >
            Mention
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className="nav-link"
            id="pluto-assigned-tab"
            data-bs-toggle="pill"
            data-bs-target="#pluto-assigned"
            type="button"
            role="tab"
            aria-controls="pluto-assigned"
            aria-selected="false"
          >
            Assigned
          </button>
        </li>
      </ul>
      <div className="tab-content" id="pluto-tabContent">
        <div
          className="tab-pane fade show active"
          id="pluto-all"
          role="tabpanel"
          aria-labelledby="pluto-all-tab"
          tabIndex="0"
        >
          <ul className="notification-list d-flex flex-column row-gap-3 overflow-x-auto">
            {allList &&
              allList.length > 0 &&
              allList.map((all) => (
                <li
                  key={all.key}
                  onClick={() =>
                    !all.read ? onUpdateReadStatus(all.gid, all.type) : {}
                  }
                >
                  <div
                    className={
                      all.type === "assignee"
                        ? "assigned-box d-flex"
                        : "notification-box d-flex"
                    }
                  >
                    <div className="avatar-img me-2">
                      {/* {all.photoURL ? (
                            <div className="img-box">
                              <img
                                src={all.photoURL}
                                alt={`${all.firstName}${all.lastName}`}
                              />
                            </div>
                          ) : (
                            getFirstLetterOfName(all)
                          )} */}

                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Popover className="max-w-unset">
                            <UserHover user={all} />
                          </Popover>
                        }
                      >
                        {all.photoURL ? (
                          <img
                            src={all.photoURL}
                            alt={`${all.firstName} ${all.lastName}`}
                            className="w-100 h-100 object-fit-cover"
                          />
                        ) : (
                          getFirstLetterOfName(all)
                        )}
                      </OverlayTrigger>
                    </div>
                    <div className="notofication-details">
                      <h6 className="notification-title text-sm font-medium leading-tight text-slate-600">
                        {all.subject}
                      </h6>
                      {all.type === "assignee" && (
                        <>
                          {all.dueDate && (
                            <div className="month-date my-3">
                              <i className="bx bx-time-five"></i>{" "}
                              {formatDateWithFullMonthTime(all.dueDate)}
                            </div>
                          )}
                          <div className="notification-btn-list d-flex flex-wrap gap-2">
                            {all.button1 && (
                              <button
                                className="px-3 py-1 text-green-700 border border-green-300 rounded bg-green-100 cursor-pointer"
                                onClick={() =>
                                  onUpdateDecision(all.taskId, all.button1)
                                }
                              >
                                {all.button1}
                              </button>
                            )}
                            {all.button2 && (
                              <button
                                className="px-3 py-1 text-red-700 border border-red-300 rounded bg-red-100 cursor-pointer"
                                onClick={() =>
                                  onUpdateDecision(all.taskId, all.button1)
                                }
                              >
                                {all.button2}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      {all.type === "request" && (
                        <div className="notification-btn-list d-flex flex-wrap gap-2">
                          {all.button1 && (
                            <button
                              className="px-3 py-1 text-green-700 border border-green-300 rounded bg-green-100 cursor-pointer"
                              onClick={() =>
                                onUpdateDecision(all.taskId, all.button1)
                              }
                            >
                              {all.button1}
                            </button>
                          )}
                          {all.button1 && (
                            <button
                              className="px-3 py-1 text-green-700 border border-green-300 rounded bg-green-100 cursor-pointer"
                              onClick={() =>
                                onUpdateDecision(all.taskId, all.button1)
                              }
                            >
                              {all.button2}
                            </button>
                          )}
                        </div>
                      )}
                      {all.type === "mentioned" && (
                        <div className="notification-text">
                          <div
                            dangerouslySetInnerHTML={{ __html: all.notes }}
                          />
                        </div>
                      )}
                      {all.type !== "assignee" && (
                        <div className="date-time">
                          {all.dueDate
                            ? formatDateWithFullMonthTime(all.dueDate)
                            : ""}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        </div>
        <div
          className="tab-pane fade"
          id="pluto-email"
          role="tabpanel"
          aria-labelledby="pluto-email-tab"
          tabIndex="0"
        >
          <ul className="email-list m-0 p-0">
            {emailList &&
              emailList.length > 0 &&
              emailList.map((email) => (
                <li
                  key={email.key}
                  onClick={() =>
                    !email.read ? onUpdateReadStatus(email.gid) : {}
                  }
                >
                  <div className="email-box d-flex list-style-none">
                    <div className="avatar-img me-2">
                      {email.photoURL ? (
                        <div className="img-box">
                          <img
                            src={email.photoURL}
                            alt={`${email.firstName}${email.lastName}`}
                          />
                        </div>
                      ) : (
                        getFirstLetterOfName(email)
                      )}
                    </div>
                    <div className="emai-short-details">
                      <span className="emailer-name">
                        {email.firstName} {email.lastName}
                      </span>
                      <h6 className="email-title">{email.subject}</h6>
                      <p>{email.text}</p>
                      <div className="date-time">
                        {formatDateWithFullMonthTime(email.creationDate)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        </div>
        <div
          className="tab-pane fade"
          id="pluto-mention"
          role="tabpanel"
          aria-labelledby="pluto-mention-tab"
          tabIndex="0"
        >
          <ul className="notification-list d-flex flex-column row-gap-3 overflow-x-auto">
            {mentionList &&
              mentionList.length > 0 &&
              mentionList.map((mention) => (
                <li
                  key={mention.key}
                  onClick={() =>
                    !mention.read ? onUpdateReadStatus(mention.gid) : {}
                  }
                >
                  <div className="notification-box d-flex">
                    <div className="avatar-img me-2">
                      {mention.photoURL ? (
                        <div className="img-box">
                          <img
                            src={mention.photoURL}
                            alt={`${mention.firstName}${mention.lastName}`}
                          />
                        </div>
                      ) : (
                        getFirstLetterOfName(mention)
                      )}
                    </div>
                    <div className="notofication-details">
                      <h6 className="notification-title text-sm font-medium leading-tight text-slate-600">
                        {mention.subject}
                      </h6>
                      <div className="notification-text">
                        <div
                          className="mb-0 text-break"
                          dangerouslySetInnerHTML={{
                            __html: mention.notes,
                          }}
                        />
                      </div>
                      <div className="date-time">
                        {mention.dueDate
                          ? formatDateWithFullMonthTime(mention.dueDate)
                          : ""}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        </div>
        <div
          className="tab-pane fade"
          id="pluto-assigned"
          role="tabpanel"
          aria-labelledby="pluto-assigned-tab"
          tabIndex="0"
        >
          <ul className="notification-list d-flex flex-column row-gap-3 overflow-x-auto">
            {assigneeList &&
              assigneeList.length > 0 &&
              assigneeList.map((assignee) => (
                <li
                  key={assignee.key}
                  onClick={() =>
                    !assignee.read ? onUpdateReadStatus(assignee.gid) : {}
                  }
                >
                  <div className="assigned-box d-flex">
                    <div className="avatar-img me-2">
                      {assignee.photoURL ? (
                        <div className="img-box">
                          <img
                            src={assignee.photoURL}
                            alt={`${assignee.firstName}${assignee.lastName}`}
                          />
                        </div>
                      ) : (
                        getFirstLetterOfName(assignee)
                      )}
                    </div>
                    <div className="notofication-details">
                      <h6 className="notification-title text-sm font-medium leading-tight text-slate-600">
                        {assignee.subject}
                      </h6>
                      {assignee.dueDate && (
                        <div className="month-date my-3">
                          <i className="bx bx-time-five"></i>{" "}
                          {formatDateWithFullMonthTime(assignee.dueDate)}
                        </div>
                      )}
                      <div className="notification-btn-list d-flex flex-wrap align-items-center gap-2">
                        {assignee.button1 && (
                          <div
                            className="px-3 py-1 text-green-700 border border-green-300 rounded bg-green-100 cursor-pointer"
                            onClick={() =>
                              onUpdateDecision(
                                assignee.taskId,
                                assignee.button1
                              )
                            }
                          >
                            {assignee.button1}
                          </div>
                        )}
                        {assignee.button1 && (
                          <div
                            className="px-3 py-1 text-red-700 border border-red-300 rounded bg-red-100 cursor-pointer"
                            onClick={() =>
                              onUpdateDecision(
                                assignee.taskId,
                                assignee.button1
                              )
                            }
                          >
                            {assignee.button2}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
